(() => {
  'use strict';

  const SELECTOR = '[data-nh-seasonal-effect]';
  const MOBILE_QUERY = '(max-width: 1024px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const IST_OFFSET = '+05:30';
  const controllers = new WeakMap();

  const intensitySettings = {
    subtle: {
      desktopInterval: [6500, 9000],
      desktopSize: [30, 40],
      desktopDoubleChance: 0.45,
      mobileInterval: [12000, 16000],
      mobileSize: [16, 20]
    },
    balanced: {
      desktopInterval: [5000, 7000],
      desktopSize: [42, 58],
      desktopDoubleChance: 1,
      mobileInterval: [9000, 12000],
      mobileSize: [20, 26]
    },
    festive: {
      desktopInterval: [3800, 5500],
      desktopSize: [48, 66],
      desktopDoubleChance: 1,
      mobileInterval: [7500, 10000],
      mobileSize: [21, 28]
    }
  };

  const palettes = {
    diwali: {
      light: ['#A84F00', '#C26800', '#6D2077', '#9B5610'],
      dark: ['#F4D35E', '#FF9F1C', '#8B3A8B', '#FFF3B0']
    },
    dasara: {
      light: ['#A85B00', '#BD6800', '#8A4B00', '#6D2077'],
      dark: ['#F4D35E', '#E9911A', '#FFC857', '#FFF0B8']
    }
  };

  const randomBetween = (minimum, maximum) => minimum + Math.random() * (maximum - minimum);

  function activePalette(preset) {
    const mode = document.documentElement.dataset.nsNightMode === 'dark' ? 'dark' : 'light';
    return palettes[preset][mode];
  }

  function parseIstBoundary(value, isEnd) {
    const normalized = String(value || '').trim();
    if (!normalized) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;

    const [year, month, day] = normalized.split('-').map(Number);
    const calendarDate = new Date(Date.UTC(year, month - 1, day));
    if (
      calendarDate.getUTCFullYear() !== year ||
      calendarDate.getUTCMonth() !== month - 1 ||
      calendarDate.getUTCDate() !== day
    ) return null;

    const time = isEnd ? 'T23:59:59.999' : 'T00:00:00.000';
    const parsed = new Date(`${normalized}${time}${IST_OFFSET}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function fireworkMarkup() {
    return `
      <circle class="nh-seasonal-burst__ring" cx="50" cy="50" r="19"></circle>
      <g class="nh-seasonal-burst__rays">
        <line x1="50" y1="28" x2="50" y2="8"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(45 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(90 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(135 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(180 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(225 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(270 50 50)"></line>
        <line x1="50" y1="28" x2="50" y2="8" transform="rotate(315 50 50)"></line>
      </g>
      <g class="nh-seasonal-burst__dots">
        <circle cx="50" cy="5" r="2.4"></circle>
        <circle cx="82" cy="18" r="2"></circle>
        <circle cx="95" cy="50" r="2.4"></circle>
        <circle cx="82" cy="82" r="2"></circle>
        <circle cx="50" cy="95" r="2.4"></circle>
        <circle cx="18" cy="82" r="2"></circle>
        <circle cx="5" cy="50" r="2.4"></circle>
        <circle cx="18" cy="18" r="2"></circle>
      </g>`;
  }

  function dasaraMarkup() {
    return `
      <g class="nh-seasonal-burst__petals">
        <ellipse cx="50" cy="21" rx="5.5" ry="15"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(45 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(90 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(135 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(180 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(225 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(270 50 50)"></ellipse>
        <ellipse cx="50" cy="21" rx="5.5" ry="15" transform="rotate(315 50 50)"></ellipse>
      </g>
      <circle class="nh-seasonal-burst__core" cx="50" cy="50" r="7"></circle>
      <g class="nh-seasonal-burst__dots">
        <circle cx="50" cy="6" r="2"></circle>
        <circle cx="94" cy="50" r="2"></circle>
        <circle cx="50" cy="94" r="2"></circle>
        <circle cx="6" cy="50" r="2"></circle>
      </g>`;
  }

  class SeasonalHeaderEffect {
    constructor(root) {
      this.root = root;
      this.desktopLayer = root.querySelector('[data-nh-seasonal-layer="desktop"]');
      this.mobileLayer = root.querySelector('[data-nh-seasonal-layer="mobile"]');
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.motionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.enabled = root.dataset.nhSeasonalEnabled === 'true';
      this.preset = root.dataset.nhSeasonalPreset === 'dasara' ? 'dasara' : 'diwali';
      this.intensity = intensitySettings[root.dataset.nhSeasonalIntensity] ? root.dataset.nhSeasonalIntensity : 'balanced';
      this.mobileEnabled = root.dataset.nhSeasonalMobile === 'true';
      this.pauseOnSticky = root.dataset.nhSeasonalPauseSticky !== 'false';
      this.startDate = parseIstBoundary(root.dataset.nhSeasonalStart, false);
      this.endDate = parseIstBoundary(root.dataset.nhSeasonalEnd, true);
      this.timer = null;
      this.secondaryTimer = null;
      this.boundaryTimer = null;
      this.introTimers = [];
      this.hasPlayedIntro = false;
      this.destroyed = false;
      this.lastSide = 'right';
      this.handleEnvironmentChange = this.handleEnvironmentChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.observer = new MutationObserver(this.handleEnvironmentChange);
      this.documentObserver = new MutationObserver(this.handleEnvironmentChange);
      this.connect();
    }

    connect() {
      this.root.dataset.nhSeasonalReady = 'true';
      this.mobileMedia.addEventListener?.('change', this.handleEnvironmentChange);
      this.motionMedia.addEventListener?.('change', this.handleMotionChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('on:cart-drawer:before-open', this.handleEnvironmentChange);
      document.addEventListener('on:cart-drawer:after-close', this.handleEnvironmentChange);
      window.addEventListener('resize', this.handleEnvironmentChange, { passive: true });
      window.addEventListener('scroll', this.handleEnvironmentChange, { passive: true });
      this.observer.observe(this.root, { attributes: true, attributeFilter: ['class'], attributeOldValue: true, subtree: true });
      this.documentObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.sync(true);
      this.scheduleBoundaryCheck();
    }

    isWithinSchedule() {
      if (!this.enabled) return false;
      const now = Date.now();
      if (this.startDate && now < this.startDate.getTime()) return false;
      if (this.endDate && now > this.endDate.getTime()) return false;
      return true;
    }

    isBlocked() {
      if (document.visibilityState !== 'visible') return true;
      if (this.mobileMedia.matches && !this.mobileEnabled) return true;
      if (this.pauseOnSticky && this.root.classList.contains('nh-is-sticky')) return true;
      if (document.documentElement.classList.contains('ns-mobile-menu-open')) return true;
      if (document.documentElement.classList.contains('ns-cart-drawer-open')) return true;
      if (this.root.querySelector('.nh-search-overlay.is-open')) return true;
      if (this.root.querySelector('.nh-nav__item.is-open')) return true;
      if (this.root.querySelector('.nh-mega-shade.is-visible')) return true;
      return false;
    }

    activeLayer() {
      return this.mobileMedia.matches ? this.mobileLayer : this.desktopLayer;
    }

    shouldDisplay() {
      return this.isWithinSchedule() && !this.isBlocked() && Boolean(this.activeLayer());
    }

    shouldDisplayMenuTheme() {
      if (!this.isWithinSchedule() || document.visibilityState !== 'visible') return false;
      if (this.mobileMedia.matches && !this.mobileEnabled) return false;
      return true;
    }

    handleEnvironmentChange(records) {
      if (this.destroyed) return;
      if (Array.isArray(records) && records.length && records.every((record) => {
        if (record.target !== this.root) return false;
        const oldClasses = String(record.oldValue || '')
          .replace(/\bnh-seasonal-(?:visible|reduced|intro|menu-active)\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const currentClasses = this.root.className
          .replace(/\bnh-seasonal-(?:visible|reduced|intro|menu-active)\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        return oldClasses === currentClasses;
      })) return;
      window.requestAnimationFrame(() => this.sync(false));
    }

    handleMotionChange() {
      this.sync(true);
    }

    handleVisibilityChange() {
      this.sync(false);
    }

    clearTimers() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.secondaryTimer);
      this.introTimers.forEach((timer) => window.clearTimeout(timer));
      this.introTimers = [];
      this.timer = null;
      this.secondaryTimer = null;
      this.root.classList.remove('nh-seasonal-intro');
    }

    clearBursts() {
      this.root.querySelectorAll('.nh-seasonal-burst').forEach((burst) => burst.remove());
    }

    sync(immediate) {
      this.clearTimers();
      const visible = this.shouldDisplay();
      const menuThemeVisible = this.shouldDisplayMenuTheme();
      const festivalActive = this.isWithinSchedule() && (!this.mobileMedia.matches || this.mobileEnabled);
      const siteThemeVisible = menuThemeVisible && this.root.classList.contains('nh-seasonal-menu-theme');
      const reduced = this.motionMedia.matches;
      this.root.classList.toggle('nh-seasonal-visible', visible);
      this.root.classList.toggle('nh-seasonal-menu-active', menuThemeVisible);
      document.documentElement.classList.toggle('ns-seasonal-festival-active', festivalActive);
      document.documentElement.classList.toggle('ns-seasonal-site-gradient-active', siteThemeVisible);
      this.root.classList.toggle('nh-seasonal-reduced', visible && reduced);

      if (!visible || reduced) {
        this.clearBursts();
        return;
      }

      if (immediate && !this.hasPlayedIntro) {
        this.playIntro();
        const settings = intensitySettings[this.intensity];
        const range = this.mobileMedia.matches ? settings.mobileInterval : settings.desktopInterval;
        this.scheduleNext(2100 + randomBetween(range[0], range[1]));
        return;
      }

      this.scheduleNext();
    }

    playIntro() {
      if (!this.shouldDisplay() || this.motionMedia.matches || this.hasPlayedIntro) return;
      this.hasPlayedIntro = true;
      this.root.classList.add('nh-seasonal-intro');

      const isMobile = this.mobileMedia.matches;
      const settings = intensitySettings[this.intensity];
      const desktopBursts = this.intensity === 'subtle' ? 4 : 5;
      const mobileBursts = this.intensity === 'subtle' ? 1 : 2;
      const count = isMobile ? mobileBursts : desktopBursts;
      const desktopPositions = [
        { x: 7, y: 52 },
        { x: 25, y: 34 },
        { x: 50, y: 62 },
        { x: 75, y: 34 },
        { x: 93, y: 52 }
      ];
      const mobilePositions = [
        { x: 9, y: 48 },
        { x: 91, y: 48 }
      ];
      const positions = isMobile ? mobilePositions : desktopPositions;

      positions.slice(0, count).forEach((position, index) => {
        const delay = isMobile ? 100 + (index * 620) : 80 + (index * 180);
        const timer = window.setTimeout(() => {
          const sizeRange = isMobile ? settings.mobileSize : settings.desktopSize;
          this.createBurst({
            x: position.x,
            y: position.y,
            size: randomBetween(sizeRange[0], sizeRange[1]) * (isMobile ? 1.08 : 1.16),
            color: activePalette(this.preset)[index % activePalette(this.preset).length],
            duration: 1100,
            intro: true
          });
        }, delay);
        this.introTimers.push(timer);
      });

      this.introTimers.push(window.setTimeout(() => {
        this.root.classList.remove('nh-seasonal-intro');
      }, 2100));
    }

    scheduleNext(explicitDelay) {
      if (!this.shouldDisplay() || this.motionMedia.matches) return;
      const settings = intensitySettings[this.intensity];
      const range = this.mobileMedia.matches ? settings.mobileInterval : settings.desktopInterval;
      const presetMultiplier = this.preset === 'dasara' ? 1.28 : 1;
      const delay = explicitDelay ?? randomBetween(range[0], range[1]) * presetMultiplier;
      this.timer = window.setTimeout(() => {
        this.spawnBurst();
        this.scheduleNext();
      }, delay);
    }

    createBurst({ x, y, size, color, duration, intro = false }) {
      if (!this.shouldDisplay() || this.motionMedia.matches) return;
      const layer = this.activeLayer();
      if (!layer) return;

      const isMobile = this.mobileMedia.matches;
      const maximumActive = intro ? (isMobile ? 2 : 5) : (isMobile ? 1 : 2);
      if (layer.querySelectorAll('.nh-seasonal-burst').length >= maximumActive) return;

      const burst = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      burst.setAttribute('class', `nh-seasonal-burst nh-seasonal-burst--${this.preset}${intro ? ' nh-seasonal-burst--intro' : ''}`);
      burst.setAttribute('viewBox', '0 0 100 100');
      burst.setAttribute('aria-hidden', 'true');
      burst.setAttribute('focusable', 'false');
      burst.style.setProperty('--nh-seasonal-x', `${x}%`);
      burst.style.setProperty('--nh-seasonal-y', `${y}%`);
      burst.style.setProperty('--nh-seasonal-size', `${size}px`);
      burst.style.setProperty('--nh-seasonal-color', color);
      burst.style.setProperty('--nh-seasonal-duration', `${duration}ms`);
      burst.innerHTML = this.preset === 'dasara' ? dasaraMarkup() : fireworkMarkup();
      layer.appendChild(burst);

      const removeBurst = () => burst.remove();
      burst.addEventListener('animationend', removeBurst, { once: true });
      window.setTimeout(removeBurst, duration + 250);
    }

    spawnBurst(preferredSide, allowSecondary = true) {
      if (!this.shouldDisplay() || this.motionMedia.matches) return;

      const isMobile = this.mobileMedia.matches;
      const settings = intensitySettings[this.intensity];
      const nextSide = preferredSide || (this.lastSide === 'left' ? 'right' : 'left');
      this.lastSide = nextSide;
      const x = isMobile
        ? (nextSide === 'left' ? randomBetween(7, 14) : randomBetween(86, 93))
        : (nextSide === 'left' ? randomBetween(5, 19) : randomBetween(81, 95));
      const y = isMobile ? randomBetween(27, 73) : randomBetween(22, 78);
      const sizeRange = isMobile ? settings.mobileSize : settings.desktopSize;
      const palette = activePalette(this.preset);

      this.createBurst({
        x,
        y,
        size: randomBetween(sizeRange[0], sizeRange[1]),
        color: palette[Math.floor(Math.random() * palette.length)],
        duration: this.preset === 'dasara' ? 1500 : 1150
      });

      if (!isMobile && allowSecondary && Math.random() < settings.desktopDoubleChance) {
        this.secondaryTimer = window.setTimeout(() => {
          this.spawnBurst(nextSide === 'left' ? 'right' : 'left', false);
        }, randomBetween(220, 380));
      }
    }

    scheduleBoundaryCheck() {
      window.clearTimeout(this.boundaryTimer);
      const now = Date.now();
      const boundaries = [];
      if (this.startDate && this.startDate.getTime() > now) boundaries.push(this.startDate.getTime());
      if (this.endDate && this.endDate.getTime() >= now) boundaries.push(this.endDate.getTime() + 1);
      if (!boundaries.length) return;

      const nextBoundary = Math.min(...boundaries);
      const delay = Math.min(Math.max(nextBoundary - now + 50, 1000), 2000000000);
      this.boundaryTimer = window.setTimeout(() => {
        this.sync(true);
        this.scheduleBoundaryCheck();
      }, delay);
    }

    destroy() {
      this.destroyed = true;
      this.clearTimers();
      window.clearTimeout(this.boundaryTimer);
      this.clearBursts();
      this.observer.disconnect();
      this.documentObserver.disconnect();
      this.mobileMedia.removeEventListener?.('change', this.handleEnvironmentChange);
      this.motionMedia.removeEventListener?.('change', this.handleMotionChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('on:cart-drawer:before-open', this.handleEnvironmentChange);
      document.removeEventListener('on:cart-drawer:after-close', this.handleEnvironmentChange);
      window.removeEventListener('resize', this.handleEnvironmentChange);
      window.removeEventListener('scroll', this.handleEnvironmentChange);
      this.root.classList.remove('nh-seasonal-visible', 'nh-seasonal-reduced', 'nh-seasonal-intro', 'nh-seasonal-menu-active');
      document.documentElement.classList.remove('ns-seasonal-festival-active', 'ns-seasonal-site-gradient-active');
      delete this.root.dataset.nhSeasonalReady;
    }
  }

  function initialize(context = document) {
    const roots = [];
    if (context.matches?.(SELECTOR)) roots.push(context);
    context.querySelectorAll?.(SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      const controller = new SeasonalHeaderEffect(root);
      controllers.set(root, controller);
    });
  }

  function destroyWithin(context) {
    const roots = [];
    if (context.matches?.(SELECTOR)) roots.push(context);
    context.querySelectorAll?.(SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
